import { z } from 'zod'

/** 分页信封 data（Go 空切片序列化为 null，做容错） */
export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    total: z.number(),
    items: z.array(item).nullable().transform((v) => v ?? []),
    page: z.number(),
    pageSize: z.number(),
  })

/** 裸数组端点的 null 容错 */
export const nullableArray = <T extends z.ZodTypeAny>(item: T) =>
  z.array(item).nullable().transform((v) => v ?? [])

// ---------- v1 实体 ----------

export const earthquakeSchema = z.object({
  id: z.number(),
  eventId: z.string(),
  magnitude: z.number().default(0),
  epicenterLat: z.number().default(0),
  epicenterLon: z.number().default(0),
  epicenterName: z.string().default(''),
  depth: z.number().default(0),
  alertLevel: z.string().default(''),
  estimatedArrivalSec: z.number().default(0),
  geohash: z.string().default(''),
  status: z.string().default('pending'),
  confirmedDevices: z.number().default(0),
  triggerCount: z.number().default(0),
  originTime: z.number(),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type Earthquake = z.infer<typeof earthquakeSchema>

export const triggerSchema = z.object({
  id: z.number(),
  triggerId: z.string(),
  eventId: z.string().default(''),
  deviceId: z.string(),
  lat: z.number().nullable().optional(),
  lon: z.number().nullable().optional(),
  iqr: z.number().default(0),
  cav: z.number().default(0),
  zcRate: z.number().default(0),
  staLtaRatio: z.number().default(0),
  bearing: z.number().nullable().optional(),
  deviceType: z.number().default(0),
  rawData: z.string().nullable().optional(),
  confirmed: z.boolean().default(false),
  createdAt: z.number().default(0),
})
export type Trigger = z.infer<typeof triggerSchema>

export const deviceSchema = z.object({
  id: z.number(),
  deviceId: z.string(),
  lat: z.number().nullable().optional(),
  lon: z.number().nullable().optional(),
  status: z.string().default('online'),
  battery: z.number().nullable().optional(),
  signalStrength: z.number().nullable().optional(),
  lastHeartbeat: z.number().default(0),
  firstSeen: z.number().default(0),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type Device = z.infer<typeof deviceSchema>

export const deviceDistItemSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  status: z.string().default('online'),
})
export type DeviceDistItem = z.infer<typeof deviceDistItemSchema>

export const reportSchema = z.object({
  id: z.number(),
  reportId: z.string(),
  eventId: z.string().default(''),
  title: z.string().default(''),
  summary: z.string().default(''),
  content: z.string().default(''),
  magnitude: z.number().default(0),
  epicenterLat: z.number().default(0),
  epicenterLon: z.number().default(0),
  epicenterName: z.string().default(''),
  affectedRadiusKm: z.number().default(0),
  triggeredDevices: z.number().default(0),
  confirmedDevices: z.number().default(0),
  estimatedLoss: z.string().default(''),
  attachments: z.string().default(''),
  status: z.string().default(''),
  originTime: z.number().default(0),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type Report = z.infer<typeof reportSchema>

export const incidentSchema = z.object({
  id: z.number(),
  reportId: z.string(),
  deviceId: z.string().default(''),
  type: z.string().default('other'),
  lat: z.number().nullable().optional(),
  lon: z.number().nullable().optional(),
  severity: z.number().default(1),
  description: z.string().default(''),
  photoUrls: z.string().default('[]'),
  eventId: z.string().default(''),
  status: z.string().default('pending'),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type Incident = z.infer<typeof incidentSchema>

export const eventSchema = z.object({
  id: z.number(),
  eventId: z.string(),
  type: z.string().default('other'),
  severity: z.number().default(1),
  lat: z.number().default(0),
  lon: z.number().default(0),
  name: z.string().default(''),
  depth: z.number().default(0),
  magnitude: z.number().default(0),
  alertLevel: z.string().default(''),
  estimatedArrivalSec: z.number().default(0),
  geohash: z.string().default(''),
  status: z.string().default('pending'),
  confirmedDevices: z.number().default(0),
  triggerCount: z.number().default(0),
  originTime: z.number(),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type Event = z.infer<typeof eventSchema>

export const realtimeStatsSchema = z.object({
  onlineDevices: z.number().default(0),
  totalDevices: z.number().default(0),
  todayTriggers: z.number().default(0),
  monthEarthquakes: z.number().default(0),
})
export type RealtimeStats = z.infer<typeof realtimeStatsSchema>

export const eventAggregateSchema = z.object({
  event: eventSchema,
  incidents: z.array(incidentSchema).nullable().transform((v) => v ?? []),
  triggers: z.array(triggerSchema).nullable().transform((v) => v ?? []),
  reports: z.array(reportSchema).nullable().transform((v) => v ?? []),
})
export type EventAggregate = z.infer<typeof eventAggregateSchema>

// ---------- v2 实体 ----------

export const v2UserSchema = z.object({
  userId: z.string(),
  subject: z.string().optional(),
  email: z.string().optional(),
  displayName: z.string().default(''),
  status: z.string().default(''),
  regionCode: z.string().optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
})
export type V2User = z.infer<typeof v2UserSchema>

export const roleSchema = z.object({
  code: z.string(),
  name: z.string().default(''),
  description: z.string().default(''),
})
export type Role = z.infer<typeof roleSchema>

export const permissionSchema = z.object({
  code: z.string(),
  description: z.string().default(''),
})
export type PermissionRecord = z.infer<typeof permissionSchema>

export const auditLogSchema = z.object({
  auditId: z.string(),
  actorId: z.string().default(''),
  action: z.string().default(''),
  resourceType: z.string().default(''),
  resourceId: z.string().optional(),
  regionCode: z.string().optional(),
  requestId: z.string().default(''),
  outcome: z.string().default(''),
  details: z.string().optional(),
  createdAt: z.number().default(0),
})
export type AuditLog = z.infer<typeof auditLogSchema>

export const volunteerApplicationSchema = z.object({
  applicationId: z.string(),
  userId: z.string(),
  regionCode: z.string().optional(),
  motivation: z.string().default(''),
  skillCodes: z.array(z.string()).default([]),
  status: z.string().default('pending'),
  reviewedBy: z.string().optional(),
  reviewedAt: z.number().nullable().optional(),
  reviewNote: z.string().optional(),
  createdAt: z.number().default(0),
  updatedAt: z.number().default(0),
})
export type VolunteerApplication = z.infer<typeof volunteerApplicationSchema>

export const volunteerTaskSchema = z.object({
  id: z.number(),
  taskId: z.string(),
  eventId: z.string().default(''),
  volunteerId: z.string().default(''),
  regionCode: z.string().optional(),
  type: z.string().default('report'),
  lat: z.number().default(0),
  lon: z.number().default(0),
  radiusKm: z.number().default(5),
  requiredCapability: z.string().default(''),
  status: z.string().default('pending'),
  assignedAt: z.number().nullable().optional(),
  acceptedAt: z.number().nullable().optional(),
  completedAt: z.number().nullable().optional(),
  rating: z.number().default(0),
  createdAt: z.number().default(0),
})
export type VolunteerTask = z.infer<typeof volunteerTaskSchema>

// ---------- v2 救援流向 / 证书 / 协议 ----------

function normalizeTs(v: number | string | null | undefined): number {
  if (v == null || v === '') return 0
  if (typeof v === 'number') return v > 0 && v < 1e11 ? v * 1000 : v
  const parsed = Date.parse(v)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** 宽松时间戳：兼容秒/毫秒数字与 RFC3339 字符串（Go time.Time 双兼容） */
export const looseTsSchema = z.union([z.number(), z.string(), z.null()]).optional().transform(normalizeTs)

/** 宽松 ID：数字或字符串统一转为 string */
const idString = z.union([z.string(), z.number()]).transform((v) => String(v))
const idStringOptional = z
  .union([z.string(), z.number(), z.null()])
  .optional()
  .transform((v) => (v == null ? '' : String(v)))
/** 宽松数值：后端可能回传字符串数字 */
const looseNumber = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === 'number' ? v : Number(v) || 0))

export const rescueTrackPointSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  recordedAt: looseTsSchema,
})
export type RescueTrackPoint = z.infer<typeof rescueTrackPointSchema>

export const rescueEvidenceSchema = z.object({
  id: idString,
  filePath: z.string().default(''),
  createdAt: looseTsSchema,
  note: z.string().nullable().optional().transform((v) => v ?? ''),
})
export type RescueEvidence = z.infer<typeof rescueEvidenceSchema>

export const rescueTaskSchema = z.object({
  taskId: idString,
  status: z.string().default('pending'),
  volunteerUserId: idStringOptional,
  track: z.array(rescueTrackPointSchema).nullable().optional().transform((v) => v ?? []),
  evidence: z.array(rescueEvidenceSchema).nullable().optional().transform((v) => v ?? []),
})
export type RescueTask = z.infer<typeof rescueTaskSchema>

export const rescueFlowSchema = z.object({
  tasks: z.array(rescueTaskSchema).nullable().optional().transform((v) => v ?? []),
})
export type RescueFlow = z.infer<typeof rescueFlowSchema>

export const volunteerCertificateSchema = z.object({
  certificateId: idString,
  userId: idString,
  taskId: idStringOptional,
  serviceMinutes: looseNumber,
  issuedAt: looseTsSchema,
  status: z.string().default('active'),
})
export type VolunteerCertificate = z.infer<typeof volunteerCertificateSchema>

/**
 * 证书列表 data 宽松解析：
 * 兼容裸数组 / {items} / {certificates|list} 三种形状，汇总字段 totalMinutes/totalCount 可选。
 */
export const volunteerCertificatesPayloadSchema = z.preprocess(
  (raw) => {
    if (Array.isArray(raw)) return { items: raw }
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>
      if (o.items == null && (o.certificates != null || o.list != null)) {
        return { ...o, items: o.certificates ?? o.list }
      }
    }
    return raw ?? { items: [] }
  },
  z.object({
    items: z.array(volunteerCertificateSchema).nullable().transform((v) => v ?? []),
    totalMinutes: looseNumber.optional(),
    totalCount: looseNumber.optional(),
  }),
)
export type VolunteerCertificatesPayload = z.infer<typeof volunteerCertificatesPayloadSchema>

export const volunteerAgreementSchema = z.object({
  version: z.string().default(''),
  title: z.string().default(''),
  content: z.string().default(''),
  updatedAt: looseTsSchema,
})
export type VolunteerAgreement = z.infer<typeof volunteerAgreementSchema>
